<template>
  <dp-container v-if="loading">
    <ProductTypeNavbar />
    <dp-loading />
  </dp-container>
  <dp-container v-else>
    <b-row v-if="isAdmin" class="pt-3 mb-0">
      <b-col cols="6">
        <b-link :to="{ name: 'AdminRedirect' }">{{ '&#8592; Back' }}</b-link>
      </b-col>
    </b-row>
    <!-- Product Type Navbar -->
    <ProductTypeNavbar />
    <!-- Collars & Leashes -->
    <section class="d-flex flex-column py-4">
      <h1 class="sr-only">{{ $t('collars.screenReaderText') }}</h1>
      <!-- Collar & Leash - Marketing Collection -->
      <MarketingContent
        v-for="(content, index) in marketingCollection"
        :key="`marketing-collection-content-${index}`"
        :content="getMarketingContent(content)"
        :content-type="getMarketingContentType(content)"
        :style="{ order: content ? content.position : 0 }"
      />
      <!-- Collar Collections -->
      <CollarLeashSelect :groups="collarProductGroups" class="mt-4" style="order: 5" />
      <section style="order: 10">
        <div class="d-flex flex-column collection-body mx-auto">
          <CollarLeashProductGroup :groups="collarFilteredGroups" :show-all-tags="false" />
        </div>
      </section>
      <!-- Collar - Marketing Info/Content -->
      <section class="my-4 marketing-content mx-auto py-4" style="order: 15">
        <b-row class="w-100 mx-auto">
          <b-col v-for="content in $t('collars.marketingContent')" :key="content.info" class="p-3" cols="6" md="4">
            <b-img :src="content.img" alt="" center class="marketing-content-img" fluid />
            <!-- eslint-disable-next-line vue/no-v-html (Make sure it's always hard-coded) -->
            <p class="mt-2 font-weight-bold text-center h3" v-html="content.info" />
          </b-col>
        </b-row>
      </section>
      <!-- Leashes Collections -->
      <CollarLeashSelect :collar="false" :groups="leashProductGroups" style="order: 20" />
      <section style="order: 25">
        <div class="d-flex flex-column collection-body mx-auto">
          <CollarLeashProductGroup :collar="false" :groups="leashFilteredGroups" :show-all-tags="false" />
        </div>
      </section>
      <!-- Leashes - Marketing Info/Content -->
      <section class="my-4 marketing-content mx-auto py-4" style="order: 30">
        <b-row class="w-100 mx-auto">
          <b-col v-for="content in $t('leashes.marketingContent')" :key="content.info" class="p-3" cols="6" md="3">
            <b-img :src="content.img" alt="" center class="marketing-content-img" fluid />
            <!-- eslint-disable-next-line vue/no-v-html (Make sure it's always hard-coded) -->
            <h3 class="mt-2 font-weight-bold text-center" v-html="content.info" />
          </b-col>
        </b-row>
      </section>
    </section>
    <section v-if="city.petOwnerStoreMessage && city.displayPetOwnerStoreMessageForCollars" class="text-center">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <b-row v-html="city.petOwnerStoreMessage" />
    </section>
  </dp-container>
</template>

<script>
import { mapState } from 'vuex';
import ProductTypeNavbar from '@/components/ProductTypeNavbar';
import { DpContainer, DpLoading } from '@docupet/component-library';
import CollarLeashSelect from '@/components/collar&leash/CollarLeashSelect';
import CollarLeashProductGroup from '@/components/collar&leash/CollarLeashProductGroup';
import { NotificationMixins } from '@/mixins/notification';
import getImage from '@/mixins/getImage';
import MarketingContent from '@/components/MarketingContent';
import { MetaTagMixins } from '@/mixins/seo/metaTags';

export default {
  name: 'Collars',
  components: {
    MarketingContent,
    CollarLeashProductGroup,
    DpContainer,
    DpLoading,
    ProductTypeNavbar,
    CollarLeashSelect,
  },
  mixins: [MetaTagMixins, NotificationMixins, getImage],
  computed: {
    ...mapState({
      cityId: (state) => state.cityId,
      city: (state) => state.user.city,
      baseUrl: (state) => state.baseUrl,
      order: (state) => state.order.order,
      collarProductGroups: (state) => state.collarProduct?.groups || [],
      leashProductGroups: (state) => state.leashProduct?.groups || [],
      loading: (state) => state.collarProduct.loading || state.leashProduct.loading || state.order.itemLoading,
      isAdmin: (state) => state.user.user.id && state.user.user.__typename === 'Administrator',
    }),
    collarFilteredGroups() {
      try {
        let groups = [];
        this.collarProductGroups.forEach((group) => groups.push({ ...group, productStyles: group.productStyles }));
        groups.sort((a, b) => (a.sort > b.sort ? 1 : -1));
        return groups;
      } catch (e) {
        return [];
      }
    },
    leashFilteredGroups() {
      const groups = [...this.leashProductGroups]?.map((group) => ({ ...group, productStyles: group.productStyles }));
      groups.sort((a, b) => (a.sort > b.sort ? 1 : -1));
      return groups;
    },
    marketingCollection() {
      return this.city?.collarsAndLeashesLandingPageMarketingCollection?.marketingContentCollectionItems;
    },
  },
  created() {
    if (!this.collarProductGroups || this.collarProductGroups?.length === 0) {
      this.$store.dispatch('collarProduct/fetchGroups', { cityId: this.city?.id || this.cityId });
    }
    if (!this.leashProductGroups || this.leashProductGroups?.length === 0) {
      this.$store.dispatch('leashProduct/fetchGroups', { cityId: this.city?.id || this.cityId });
    }
    this.$store.dispatch('collarProduct/clearStyle');
    this.$store.dispatch('leashProduct/clearStyle');
  },
  methods: {
    getMarketingContent(content) {
      return content?.marketingContent;
    },
    getMarketingContentType(content) {
      return this.getMarketingContent(content)?.type;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.collection-body {
  max-width: 1024px;
}
.marketing-content {
  background-color: $dp-gray-1;
  max-width: 1024px;
  .marketing-content-img {
    border-radius: 12.5px;
  }
}
</style>
